import React, { useEffect, useRef } from 'react';
import { HistoricalTestResult } from '../types';
import { SaveIcon } from 'shared-icons';
// FIX: Updated import path to use the shared 'shared-grading' package.
import { formatSpeed, calculateLinkQuality } from 'shared-grading';

// Chart.js is loaded dynamically, declare the global variable for TypeScript.
declare const Chart: any;

// Helper to load a script dynamically and ensure it's only added once.
const loadScript = (src: string, id: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If script already exists, resolve immediately
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Script load error for ${src}`));
    document.body.appendChild(script);
  });
};

interface HistoryChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: HistoricalTestResult[];
}

const HistoryChartModal: React.FC<HistoryChartModalProps> = ({ isOpen, onClose, history }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);

  const watermarkPlugin = {
    id: 'watermark',
    afterDraw: (chart: any) => {
      const { ctx, chartArea: { right, bottom } } = chart;
      if (!right || !bottom) return;

      ctx.save();
      const isDarkMode = document.documentElement.classList.contains('dark');
      ctx.fillStyle = isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
      ctx.font = '14px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText('teztel.com', right, bottom);
      ctx.restore();
    }
  };

  const handleSaveChart = () => {
    const canvas = chartRef.current;
    if (!canvas) return;

    // Create a temporary canvas to add a background color.
    // The watermark is already on the main canvas thanks to the plugin.
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');

    if (tempCtx) {
      const isDarkMode = document.documentElement.classList.contains('dark');
      tempCtx.fillStyle = isDarkMode ? '#1e293b' : '#ffffff';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Draw the chart (which includes the watermark) onto the temporary canvas
      tempCtx.drawImage(canvas, 0, 0);
    }
    
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const filename = `teeznet_pk_speed_history_${timestamp}.png`;

    const link = document.createElement('a');
    link.download = filename;
    link.href = tempCanvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  useEffect(() => {
    if (!isOpen || history.length < 2) {
      return;
    }

    const initializeChart = async () => {
        try {
            await loadScript('https://cdn.jsdelivr.net/npm/chart.js@4.4.2/dist/chart.umd.min.js', 'chartjs-script');

            if (typeof Chart === 'undefined' || !chartRef.current) {
                console.error("Chart.js is not available or canvas is not ready.");
                return;
            }

            // Use the last 10 tests for clarity, and reverse for chronological order
            const chartHistory = [...history].slice(0, 10).reverse();

            const labels = chartHistory.map(h => new Date(h.date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }));
            const downloadData = chartHistory.map(h => h.download);
            const uploadData = chartHistory.map(h => h.upload);
            const pingData = chartHistory.map(h => h.ping);
            const jitterData = chartHistory.map(h => h.jitter);

            const ctx = chartRef.current.getContext('2d');
            
            // Destroy previous chart instance if it exists
            if (chartInstanceRef.current) {
              chartInstanceRef.current.destroy();
            }

            const isDarkMode = document.documentElement.classList.contains('dark');
            const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
            const textColor = isDarkMode ? '#94a3b8' : '#475569'; // slate-400 / slate-600
            const legendColor = isDarkMode ? '#e2e8f0' : '#1e293b'; // slate-200 / slate-800
            const tooltipBgColor = isDarkMode ? '#1e293b' : '#ffffff';
            const tooltipTextColor = isDarkMode ? '#e2e8f0' : '#1e293b';
            const tooltipTitleColor = isDarkMode ? '#cbd5e1' : '#334155';

            chartInstanceRef.current = new Chart(ctx, {
              type: 'line',
              data: {
                labels: labels,
                datasets: [
                  {
                    label: 'Download',
                    data: downloadData,
                    borderColor: '#06b6d4', // cyan-500
                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                    yAxisID: 'yMbps',
                    tension: 0.3,
                    fill: true,
                    pointBackgroundColor: '#06b6d4',
                    pointHoverRadius: 6,
                  },
                  {
                    label: 'Upload',
                    data: uploadData,
                    borderColor: '#a855f7', // purple-500
                    backgroundColor: 'rgba(168, 85, 247, 0.1)',
                    yAxisID: 'yMbps',
                    tension: 0.3,
                    fill: true,
                    pointBackgroundColor: '#a855f7',
                    pointHoverRadius: 6,
                  },
                  {
                    label: 'Ping',
                    data: pingData,
                    borderColor: '#f59e0b', // amber-500
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    yAxisID: 'yPing',
                    tension: 0.3,
                    pointBackgroundColor: '#f59e0b',
                    pointHoverRadius: 6,
                  },
                  {
                    label: 'Jitter',
                    data: jitterData,
                    borderColor: '#64748b', // slate-500
                    backgroundColor: 'rgba(100, 116, 139, 0.1)',
                    yAxisID: 'yPing',
                    tension: 0.3,
                    pointBackgroundColor: '#64748b',
                    pointHoverRadius: 6,
                    borderDash: [5, 5],
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                scales: {
                  x: {
                    grid: { color: gridColor },
                    ticks: { color: textColor }
                  },
                  yMbps: {
                    type: 'linear',
                    position: 'left',
                    title: {
                      display: true,
                      text: 'Speed (Mbps)',
                      color: textColor,
                    },
                    grid: { color: gridColor },
                    ticks: { color: textColor }
                  },
                  yPing: {
                    type: 'linear',
                    position: 'right',
                    title: {
                      display: true,
                      text: 'Latency (ms)',
                      color: textColor,
                    },
                    grid: {
                      drawOnChartArea: false, // only draw grid for left axis
                    },
                    ticks: { color: textColor }
                  }
                },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            color: legendColor,
                            padding: 20,
                            font: { size: 14 }
                        }
                    },
                    tooltip: {
                        backgroundColor: tooltipBgColor,
                        titleColor: tooltipTitleColor,
                        bodyColor: tooltipTextColor,
                        borderColor: gridColor,
                        borderWidth: 1,
                        padding: 10,
                        callbacks: {
                            title: function(tooltipItems: any) {
                                return tooltipItems[0]?.label || '';
                            },
                            beforeBody: function(tooltipItems: any) {
                                const dataIndex = tooltipItems[0]?.dataIndex;
                                if (dataIndex !== undefined && chartHistory[dataIndex]) {
                                    const testResult = chartHistory[dataIndex];
                                    const quality = calculateLinkQuality(testResult);
                                    return [
                                        `Grade: ${quality.grade} (${quality.title})`,
                                        `ISP: ${testResult.isp}`,
                                        `Server: ${testResult.serverName}`,
                                        '' // Spacer line
                                    ];
                                }
                                return [];
                            },
                            label: function(context: any) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    const isPingAxis = context.dataset.yAxisID === 'yPing';
                                    if (isPingAxis) {
                                        label += context.parsed.y.toFixed(0) + ' ms';
                                    } else {
                                        const formatted = formatSpeed(context.parsed.y);
                                        label += `${formatted.value} ${formatted.unit}`;
                                    }
                                }
                                return label;
                            }
                        }
                    }
                }
              },
              plugins: [watermarkPlugin],
            });
        } catch(error) {
            console.error("Failed to load or initialize chart:", error);
        }
    };

    initializeChart();

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [isOpen, history]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-chart-title"
      style={{ animation: 'modal-fade-in 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
    >
      <div
        className="bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl h-full max-h-[80vh] p-6 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modal-content-pop 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
      >
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 id="history-chart-title" className="text-xl font-bold text-slate-900 dark:text-white">
            Speed Test Trends (Last {Math.min(10, history.length)} Tests)
          </h2>
          <div className="flex items-center gap-2">
            {history.length >= 2 && (
              <button
                onClick={handleSaveChart}
                className="p-2 bg-gray-600/10 text-slate-700 dark:bg-gray-600/50 dark:text-white rounded-lg shadow-md hover:bg-gray-600/20 dark:hover:bg-gray-700/60 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-75 transition-all"
                aria-label="Save chart as image"
                title="Save as Image"
              >
                <SaveIcon />
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-3xl leading-none" aria-label="Close modal">&times;</button>
          </div>
        </div>
        <div className="flex-grow relative">
            {history.length < 2 ? (
                <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                    Run at least two tests to see a trend chart.
                </div>
            ) : (
                <div
                  className="w-full h-full"
                  onContextMenu={(e) => e.preventDefault()}
                  title="Right-click is disabled. Please use the 'Save as Image' button."
                >
                  <canvas ref={chartRef}></canvas>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default HistoryChartModal;